import React, { useMemo, useState } from 'react';
import { Pill } from '../../../domain/status';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import { shuffle } from '../../../util/array';
import { VisuallyHidden } from '../../Common/VisuallyHidden';
import { useDialog } from '../../../hooks/useDialog';
import { QualityLevel } from './PrescriptionPreview';
import backgroundUrl from '../../../assets/images/prescription.png';

const useStyles = createUseStyles({
    root: {
        display: 'flex',
        fontFamily: 'var(--font-script)',
        flexDirection: 'column',
        width: '90%',
        height: '90%',
        boxSizing: 'border-box',
        gap: '0.5rem',
        '& .qd': {
            filter: 'blur(4px)',
        },
        position: 'relative',
    },
    title: {
        top: '15%',
        left: '12%',
        width: '50%',
        transform: 'rotate(4deg)',
        margin: 0,
        fontSize: '1.3rem',
        position: 'absolute',
    },
    description: {
        fontFamily: 'var(--font-script)',
        textAlign: '',
        position: 'absolute',
        transform: 'rotate(-3deg)',
        fontSize: '1.1rem',
        top: '43.5%',
        left: '15%',
        width: '70%',
    },
    dialog: {
        width: '500px',
        padding: 0,
        backgroundImage: `url(${backgroundUrl})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundColor: 'transparent',
        aspectRatio: 0.67,
    },
    closeButton: {
        width: '50px',
        height: '50px',
        backgroundImage: 'var(--button-cross-url)',
        backgroundColor: 'transparent',
        border: 'none',
    },
    closeButtonWrapper: {
        position: 'absolute',
        top: '5%',
        right: '-3%',
    },
});

interface IPrescriptionProps {
    pill: Pill;
    quality: QualityLevel,
}

const degradeText = (originalText: string, quality: QualityLevel): React.ReactNode[] => {
    const parts = originalText.split(/(\d§|§\d)/g);
    const nodes: React.ReactNode[] = [];
    let delimiterLevel = 0;
    for (let i = 0; i < parts.length; i++) {
        const isDelimiter = i % 2 === 1;
        const part = parts[i];
        if (isDelimiter) {
            // probably closing delimiter
            if (delimiterLevel > 0) {
                delimiterLevel = 0;
            } else {
                delimiterLevel = Number(part[0]);
                if (Number.isNaN(delimiterLevel)) {
                    throw new Error('Invalid marker, expecting 1§ - 4§');
                }
            }
        } else {
            if (delimiterLevel > 0 && quality >= delimiterLevel) {
                const scrambledText = shuffle(part.split('')).join('');
                nodes.push(
                    <span key={i}>
                        <span className={`qd q${delimiterLevel}`} aria-hidden="true">{scrambledText}</span>
                        <VisuallyHidden>…</VisuallyHidden>
                    </span>
                );
            } else {
                // join subsequent text-nodes
                if (typeof nodes[nodes.length - 1] === 'string') {
                    nodes[nodes.length - 1] += part;
                } else {
                    nodes.push(part);
                }
            }
        }
    }
    return nodes;
};

export const PrescriptionDialog: React.FunctionComponent<IPrescriptionProps> = (props) => {
    const { t } = useTranslation();
    const styles = useStyles();

    const originalDescription = t(`effect.${props.pill.key}.description`);
    const description = degradeText(originalDescription, props.quality);

    const originalPrescription = t(`effect.${props.pill.key}.prescription`);
    const prescription = degradeText(originalPrescription, props.quality);

    return (
        <article className={styles.root}>
            <h2 className={styles.title}>{t('prescription.title', { value: t(`effect.${props.pill.key}.name`) })}</h2>
            {props.quality > 0 && (
                <VisuallyHidden>{t('prescription.degradationNotice')}</VisuallyHidden>
            )}
            <div className={styles.description}>
                <p>
                    {description}
                </p>
                <p>
                    {t('prescription.duration', { value: props.pill.duration })}
                </p>
                <p>
                    {prescription}
                </p>
            </div>
            <form method="dialog" className={styles.closeButtonWrapper}>
                <button
                    className={styles.closeButton}
                    aria-label={t('close')}
                ></button>
            </form>
        </article>
    );
};

export const usePrescription = (pill: Pill, quality: QualityLevel) => {
    const styles = useStyles();

    const dialogProps = useMemo(() => ({
        children: <PrescriptionDialog pill={pill} quality={quality}/>,
        className: styles.dialog,
    }), [ pill, quality ]);

    return useDialog(dialogProps);
};
